import { Request } from '../../request';
import type { IPracticeSaveRequest, IPracticeByProviderQuery } from 'types/marketing';

export type {
    IGPPracticeItem,
    IGPPracticesResp,
    IGPDetail,
    IGPDetailInsurance,
    IHVAItem,
    IHVAAssignedProviders,
    IHVAOtherPractice,
    IStaffListItem,
    ITeamMember,
    ITeamMemberAssignedProviders,
    IStaffDetail,
    IStaffMembership,
} from 'types/operation';

export interface IGPPracticesParams {
    current: number;
    size: number;
    listingStatus?: string[];
    states?: string[];
    name?: string;
    adminKeyword?: string;
}

const buildGPPracticesQueryString = (params: IGPPracticesParams): string => {
    const parts: string[] = [];
    parts.push(`current=${params.current}`);
    parts.push(`size=${params.size}`);
    if (params.name) parts.push(`name=${encodeURIComponent(params.name)}`);
    if (params.adminKeyword) parts.push(`adminKeyword=${encodeURIComponent(params.adminKeyword)}`);
    if (params.listingStatus) {
        params.listingStatus.forEach((s) => parts.push(`listingStatus=${encodeURIComponent(s)}`));
    }
    if (params.states) {
        params.states.forEach((s) => parts.push(`states=${encodeURIComponent(s)}`));
    }
    return parts.join('&');
};

export const getGPPracticesList = (params: IGPPracticesParams) => {
    const qs = buildGPPracticesQueryString(params);
    return Request.get({ url: `/admin-server/admin/group-practice/practices?${qs}` });
};

export const updateGPListingStatus = (
    organizationId: number,
    data: { targetStatus: 'LISTED' | 'PAUSE_LISTED' | 'UNLISTED' },
) => Request.post({ url: `/admin-server/admin/group-practice/practices/${organizationId}/listing`, params: data });

export const getGPDetail = (organizationId: number) =>
    Request.get({ url: `/admin-server/admin/group-practice/practices/${organizationId}/detail` });

// HVA

export interface IHVAListParams {
    current: number;
    size: number;
    name?: string;
    organizationIds?: number[];
    statuses?: string[];
    loginStatuses?: string[];
    roleCodes?: string[];
}

const buildHVAQueryString = (params: IHVAListParams): string => {
    const parts: string[] = [];
    parts.push(`current=${params.current}`);
    parts.push(`size=${params.size}`);
    if (params.name) parts.push(`name=${encodeURIComponent(params.name)}`);
    if (params.organizationIds) {
        params.organizationIds.forEach((id) => parts.push(`organizationIds=${id}`));
    }
    if (params.statuses) {
        params.statuses.forEach((st) => parts.push(`statuses=${encodeURIComponent(st)}`));
    }
    if (params.loginStatuses) {
        params.loginStatuses.forEach((ls) => parts.push(`loginStatuses=${encodeURIComponent(ls)}`));
    }
    if (params.roleCodes) {
        params.roleCodes.forEach((r) => parts.push(`roleCodes=${encodeURIComponent(r)}`));
    }
    return parts.join('&');
};

export const getHVAList = (params: IHVAListParams) => {
    const qs = buildHVAQueryString(params);
    return Request.get({ url: `/admin-server/admin/group-practice/hvaPage?${qs}` });
};

export interface IStaffListParams {
    current: number;
    size: number;
    name?: string;
    organizationIds?: number[];
    statuses?: string[];
    roleCodes?: string[];
}

export const getStaffList = (params: IStaffListParams) => {
    const qs = buildHVAQueryString(params);
    return Request.get({ url: `/admin-server/admin/group-practice/staffPage?${qs}` });
};

export const updateStaffOverallStatus = (userId: number, data: { status: 'ACTIVE' | 'DISABLED' }) =>
    Request.post({ url: `/admin-server/admin/group-practice/staff/${userId}/status`, params: data });

export const deactivateHVA = (userId: number) =>
    Request.post({ url: `/admin-server/admin/group-practice/hva/${userId}/deactivate` });

export const reactivateHVA = (userId: number) =>
    Request.post({ url: `/admin-server/admin/group-practice/hva/${userId}/reactivate` });

// Staff / Team Management

export interface ITeamListParams {
    current: number;
    size: number;
    statuses?: string;
    roleCodes?: string;
    providerIds?: string;
    allProviders?: boolean;
    portalStatuses?: string;
}

export const getTeamMemberList = (organizationId: number, params: ITeamListParams) =>
    Request.get({ url: `/admin-server/admin/group-practice/practices/${organizationId}/team/listPage`, params });

export const getTeamCountsSummary = (organizationId: number) =>
    Request.get({ url: `/admin-server/admin/group-practice/practices/${organizationId}/counts-summary` });

export const updateTeamMemberRole = (organizationId: number, memberId: number, data: { roleId: number }) =>
    Request.put({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/${memberId}/role`,
        params: data,
    });

export const updateTeamMemberScope = (organizationId: number, memberId: number, data: { providerIds: number[] }) =>
    Request.put({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/${memberId}/scope`,
        params: data,
    });

export const updateTeamMemberInfo = (
    organizationId: number,
    memberId: number,
    data: { firstName: string; lastName: string; phoneNumber?: string | null },
) =>
    Request.put({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/${memberId}/info`,
        params: data,
    });

export const updateTeamMemberStatus = (
    organizationId: number,
    memberId: number,
    data: { status: 'ACTIVE' | 'DISABLED' },
) =>
    Request.post({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/${memberId}/status`,
        params: data,
    });

export const resendTeamMemberInvite = (organizationId: number, memberId: number) =>
    Request.post({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/${memberId}/resend-invite`,
        params: {},
    });

export const resumeTeamMemberListing = (organizationId: number, memberId: number) =>
    Request.post({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/${memberId}/resume-listing`,
        params: {},
    });

export const pauseTeamMemberListing = (
    organizationId: number,
    memberId: number,
    data?: { pauseDays?: number | null },
) =>
    Request.post({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/${memberId}/pause-listing`,
        params: data || {},
    });

// Add Staff

export interface IOpsAddStaffParams {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string | null;
    roleId: number;
    isAdmin?: boolean;
    providerIds?: number[];
}

export const addOpsStaff = (organizationId: number, data: IOpsAddStaffParams) =>
    Request.post({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/add-staff`,
        params: data,
    });

export const checkOpsStaffEmail = (organizationId: number, email: string) =>
    Request.get({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/team/check-email`,
        params: { email },
    });

export const getOpsTeamMemberRoles = () => Request.get({ url: `/admin-server/admin/group-practice/roles` });

// Staff Detail

export interface IUpdateStaffMembershipParams {
    isAdmin?: boolean | null;
    phoneNumber?: string | null;
    providerIds?: number[] | null;
    roleId?: number | null;
}

export const updateStaffMembership = (userId: number, memberId: number, data: IUpdateStaffMembershipParams) =>
    Request.put({ url: `/admin-server/admin/group-practice/staff/${userId}/memberships/${memberId}`, params: data });

export const getStaffDetail = (userId: number) =>
    Request.get({ url: `/admin-server/admin/group-practice/staff/${userId}` });

// Pro-Listings (ad) — OPS token, any org. Mirror the provider /provider/practice/ad/* endpoints, scoped by organizationId.

export const getGPAdOverview = (organizationId: number) =>
    Request.get({ url: `/admin-server/admin/group-practice/practices/${organizationId}/ad/overview` });

export const getGPAdPoolBudget = (organizationId: number) =>
    Request.get({ url: `/admin-server/admin/group-practice/practices/${organizationId}/ad/budgets` });

export const saveGPAdPoolBudget = (organizationId: number, data: IPracticeSaveRequest) =>
    Request.post({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/ad/budgets/saveOrUpdate`,
        params: data,
    });

export const getGPAdByProviderList = (organizationId: number, data: IPracticeByProviderQuery) =>
    Request.post({
        url: `/admin-server/admin/group-practice/practices/${organizationId}/ad/byProvider/list`,
        params: data,
    });

export const getOpsCurrentUserByProviderId = (providerId: number) =>
    Request.get({ url: `/admin-server/admin/ops/currentUser`, params: { providerId } });
